import { useRef, useState } from 'react';

import { WarningOutlined } from '@ant-design/icons';
import { Alert, Form, Input, Radio } from 'antd';
import Select from 'antd/lib/select';
import { useUpdateEffect } from 'react-use';
import styled from 'styled-components';

import { TagInput } from 'components/common/antd/TagInput';
import { ASIN_AUDITS_UPDATE, useFeatureFlag } from 'lib/configcat';
import { getIntlStrings as getStrings } from 'locales/i18n/localeHelpers';
import { useLocale } from 'locales/LocalizationWrapper';

import { ASIN_LIMIT, COMPETITORS_LIMIT, SELLER_ID_LIMIT, validateAsin, validateSellerId } from './constants';
import { AMAZON_MARKETPLACES } from '../../../consts/consts';

export const AUDIT_SEARCH_TYPES = {
  SELLER_ID: 'SELLER_ID',
  ASIN: 'ASIN',
};

const options = [
  { label: getStrings('asinAudit.sellerId.search'), value: AUDIT_SEARCH_TYPES.SELLER_ID },
  { label: getStrings('asinAudit.asin.search'), value: AUDIT_SEARCH_TYPES.ASIN },
];

const Style = styled.div`
  display: grid;
  row-gap: 0.5rem;
  > * {
    margin-bottom: 0;
  }
`;

const CompetitorsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  column-gap: 0.5rem;
`;

export const AuditSearch = () => {
  const form = Form.useFormInstance();
  const searchType = Form.useWatch('searchType', form);
  const sellerIds = Form.useWatch('sellerIds', form);
  const asins = Form.useWatch('asins', form);
  const sellerIdsInputRef = useRef();
  const asinsInputRef = useRef();
  const [allowFocus, setAllowFocus] = useState(false);
  const [isAuditUpdateEnabled] = useFeatureFlag(ASIN_AUDITS_UPDATE);
  const { getIntlStrings } = useLocale();

  const validAsins = asins?.filter((x) => validateAsin(x)) ?? [];

  useUpdateEffect(() => {
    if (allowFocus) {
      if (searchType === AUDIT_SEARCH_TYPES.SELLER_ID) {
        sellerIdsInputRef?.current?.focus();
      } else if (searchType === AUDIT_SEARCH_TYPES.ASIN) {
        asinsInputRef?.current?.focus();
      }
    } else {
      setAllowFocus(true);
    }
  }, [searchType]);

  return (
    <Style>
      <Form.Item name="searchType">
        <Radio.Group>
          {options.map((option) => (
            <Radio key={option.value} value={option.value}>
              {option.label}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>
      {searchType === AUDIT_SEARCH_TYPES.SELLER_ID && (
        <>
          <span style={{ marginBottom: '0.25rem' }}>{getIntlStrings('asinAudit.sellerId.searchDescription')}</span>
          <Form.Item
            name="sellerIds"
            rules={[
              () => ({
                validator(_, value) {
                  return value.filter((x) => validateSellerId(x))?.length
                    ? Promise.resolve()
                    : Promise.reject(new Error(getIntlStrings('asinAudit.sellerId.requiredError')));
                },
              }),
            ]}
          >
            <TagInput
              maxTags={SELLER_ID_LIMIT}
              placeholder={getIntlStrings('asinAudit.sellerId.placeholder')}
              validate={validateSellerId}
              autoCapitalize="characters"
              ref={sellerIdsInputRef}
            />
          </Form.Item>
          {sellerIds?.length > 0 && !validateSellerId(sellerIds[0]) && (
            <Alert
              message={getIntlStrings('asinAudit.sellerId.error')}
              type="error"
              showIcon
              icon={<WarningOutlined />}
            />
          )}
        </>
      )}
      {searchType === AUDIT_SEARCH_TYPES.ASIN && (
        <>
          <span style={{ marginBottom: '0.25rem' }}>
            {getIntlStrings('asinAudit.asin.searchDescription', { max: ASIN_LIMIT })}
          </span>
          <Form.Item
            name="asins"
            rules={[
              () => ({
                validator(_, value) {
                  return value.filter((x) => validateAsin(x))?.length
                    ? Promise.resolve()
                    : Promise.reject(new Error(getIntlStrings('asinAudit.asin.requiredError')));
                },
              }),
            ]}
          >
            <TagInput
              maxTags={ASIN_LIMIT}
              placeholder={getIntlStrings('asinAudit.asin.placeholder', { max: ASIN_LIMIT })}
              validate={validateAsin}
              autoCapitalize="characters"
              ref={asinsInputRef}
            />
          </Form.Item>
          {asins?.filter((x) => !validateAsin(x))?.length > 0 && (
            <Alert
              message={getIntlStrings('asinAudit.asin.error')}
              description={getIntlStrings('asinAudit.asin.errorDescription')}
              type="error"
              showIcon
              icon={<WarningOutlined />}
            />
          )}

          {isAuditUpdateEnabled && validAsins.length > 0 && (
            <>
              <span style={{ marginTop: '1rem', marginBottom: '0.25rem' }}>
                {getIntlStrings('asinAudit.asin.competitorsDescription')}
              </span>
              {validAsins.map((asin, index) => (
                <CompetitorsContainer key={asin}>
                  <span style={{ lineHeight: '40px', fontWeight: 500 }}>
                    {getIntlStrings('asinAudit.asin.label')} {index + 1}
                  </span>
                  <Input size="large" value={asin} disabled style={{ width: '140px' }} />
                  <Form.Item
                    name={['competitors', asin]}
                    help={
                      form.getFieldError(['competitors', asin])[0] ?? getIntlStrings('asinAudit.asin.competitorsHelp')
                    }
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) {
                            return Promise.resolve();
                          }

                          const invalidCompetitors = value.filter((x) => !validateAsin(x));

                          if (invalidCompetitors.length > 0) {
                            return Promise.reject(new Error(getIntlStrings('asinAudit.asin.error')));
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ margin: 0, flex: 1 }}
                  >
                    <TagInput
                      maxTags={COMPETITORS_LIMIT}
                      placeholder={getIntlStrings('asinAudit.asin.competitorsPlaceholder')}
                      validate={validateAsin}
                      autoCapitalize="characters"
                      icon={null}
                    />
                  </Form.Item>
                </CompetitorsContainer>
              ))}
            </>
          )}
        </>
      )}
      <Form.Item name="marketplaceId" label={getIntlStrings('asinAudit.marketplace.label')} colon={false}>
        <Select showSearch>
          {AMAZON_MARKETPLACES.filter((m) => m.auditAvailable).map(({ id, flag, name }) => (
            <Select.Option value={id}>{`${flag} ${name}`}</Select.Option>
          ))}
        </Select>
      </Form.Item>
    </Style>
  );
};
